import { Seo } from '../components/Seo';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PopularFlights } from '../components/PopularFlights';
import { FlightSearchBar } from '../components/FlightSearchBar';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { DealCard } from '../components/OfferCards';
import { ItineraryCard } from '../components/ItineraryCard';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { openDeal } from '../lib/deals';
import { ShieldCheck, Sparkles, TrendingUp, Plane, Hotel, Ticket, ArrowRight, BadgeCheck, Megaphone, Stamp, ShoppingBag, Globe2, Map, Wifi, Car, CarTaxiFront, Tag } from 'lucide-react';
import { TourCard } from '../components/TourCard';

export default function Home() {
  const { currency } = useApp();
  const [heroTab, setHeroTab] = useState('search');
  const [deals, setDeals] = useState([]);
  const [sections, setSections] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [featItins, setFeatItins] = useState([]);
  const [featCreators, setFeatCreators] = useState([]);
  const [featTours, setFeatTours] = useState([]);

  useEffect(() => {
    api.track('homepage_view');
    api.publicHomepage().then(({ data }) => setSections(data.sections || [])).catch(() => {});
    api.deals().then(({ data }) => setDeals((data.deals || []).slice(0, 6))).catch(() => {});
    api.destinations().then(({ data }) => setDestinations(data.destinations.slice(0, 6))).catch(() => {});
    api.featuredItineraries(6).then(({ data }) => setFeatItins(data.itineraries)).catch(() => {});
    api.featuredCreators(6).then(({ data }) => setFeatCreators(data.creators)).catch(() => {});
    api.toursFeatured().then(({ data }) => setFeatTours((data.tours || []).slice(0, 4))).catch(() => {});
  }, []);

  return (
    <Layout>
      <Seo path="/" jsonLd={{
        '@context': 'https://schema.org', '@type': 'WebSite', name: 'FlightDeals.in',
        url: 'https://flightdeals.in', potentialAction: {
          '@type': 'SearchAction', target: 'https://flightdeals.in/flights?q={query}', 'query-input': 'required name=query',
        },
      }} />
      {/* Mobile-only quick-access vertical grid — shown FIRST on phones (above the flight
          booking hero) so users can jump to every vertical as a tappable tile. Hidden on md+. */}
      <section className="md:hidden max-w-7xl mx-auto px-4 pt-4 pb-1" data-testid="mobile-vertical-grid">
        <h2 className="text-base font-display font-semibold mb-3">Explore everything</h2>
        <div className="grid grid-cols-4 gap-2.5">
          {[
            { to: '/flights', icon: Plane, t: 'Flights' },
            { to: '/hotels', icon: Hotel, t: 'Stays' },
            { to: '/experiences', icon: Ticket, t: 'Experiences' },
            { to: '/tours', icon: Map, t: 'Tours', tag: 'New' },
            { to: '/esim', icon: Wifi, t: 'eSIM' },
            { to: '/visa', icon: Stamp, t: 'Visa' },
            { to: '/store', icon: ShoppingBag, t: 'Store', tag: 'New' },
            { to: '/deals', icon: Tag, t: 'Deals' },
            { to: '/insurance', icon: ShieldCheck, t: 'Insurance' },
            { to: '/transfers', icon: CarTaxiFront, t: 'Transfers' },
            { to: '/car-rentals', icon: Car, t: 'Car Rentals' },
            { to: '/ai-travel-planner', icon: Sparkles, t: 'AI Planner' },
          ].map((v) => (
            <Link
              key={v.to + v.t}
              to={v.to}
              data-testid={`mobile-vertical-${v.t.toLowerCase().replace(/ /g, '-')}`}
              className="relative flex flex-col items-center justify-start gap-1.5 rounded-2xl border border-border bg-card p-2.5 card-elevate active:scale-95 transition-transform"
            >
              {v.tag && (
                <span className="absolute top-1 right-1 text-[8px] font-bold leading-none text-accent-foreground bg-accent px-1.5 py-0.5 rounded-full">{v.tag}</span>
              )}
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-accent/10 text-accent">
                <v.icon className="h-[22px] w-[22px]" />
              </span>
              <span className="text-[11px] font-medium text-center leading-tight text-foreground">{v.t}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 h-[260px] sm:h-[440px]">
          <img src="https://images.pexels.com/photos/34870508/pexels-photo-34870508.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-20 pb-8">
          <div className="max-w-2xl text-primary-foreground">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px] sm:text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> Metasearch · Compare · Book with partners
            </span>
            <h1 className="mt-2.5 font-display text-2xl sm:text-5xl font-semibold tracking-tight leading-[1.1] sm:leading-[1.05]">
              Compare flights, hotels & experiences.
            </h1>
            <p className="mt-2 text-sm sm:text-base text-primary-foreground/80">
              We search dozens of deals and rank them with our transparent FlightDeals Score. No booking fees, ever.
            </p>
          </div>
          <div className="mt-6 rounded-2xl border border-border bg-card p-3 sm:p-4 shadow-lg" data-testid="home-flight-search-panel">
            <FlightSearchBar />
          </div>
          <div className="mt-3"><AffiliateDisclosure variant="footer" /></div>
        </div>
      </section>

      <PopularFlights />

      {/* Trust strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: BadgeCheck, t: 'No booking fees', s: 'We never charge you' },
            { icon: ShieldCheck, t: 'Book on partner sites', s: 'Secure & trusted' },
            { icon: TrendingUp, t: 'FlightDeals Score', s: 'Transparent 0–100 ranking' },
            { icon: Sparkles, t: 'AI Trip Planner', s: 'Plan smarter in seconds' },
          ].map((f) => (
            <div key={f.t} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/10 text-accent"><f.icon className="h-5 w-5" /></span>
              <div><p className="text-sm font-semibold">{f.t}</p><p className="text-xs text-muted-foreground">{f.s}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* Product lanes */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { to: '/flights', icon: Plane, t: 'Flights', s: 'Metasearch across airlines & partners', img: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80' },
            { to: '/hotels', icon: Hotel, t: 'Hotels', s: 'Compare stays by price & rating', img: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80' },
            { to: '/experiences', icon: Ticket, t: 'Experiences', s: 'Tours, food, adventure & more', img: 'https://images.unsplash.com/photo-1527631746610-bca00a040d60?w=800&q=80' },
          ].map((l) => (
            <Link key={l.to} to={l.to} className="group relative rounded-2xl overflow-hidden h-40 card-elevate" data-testid={`lane-${l.t.toLowerCase()}`}>
              <img src={l.img} alt={l.t} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/85 to-primary/10" />
              <div className="absolute bottom-4 left-4 text-primary-foreground">
                <l.icon className="h-5 w-5 mb-1 text-accent" />
                <p className="font-display font-semibold text-xl">{l.t}</p>
                <p className="text-xs opacity-85">{l.s}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* New verticals: Visa · Travel Store · Explore by Country */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="home-new-verticals">
          {[
            { to: '/visa', icon: Stamp, t: 'Visa Checker', s: 'Entry requirements for your passport & destination', tag: 'New' },
            { to: '/store', icon: ShoppingBag, t: 'Travel Store', s: 'Creator gear & essentials, price-compared', tag: 'New' },
            { to: '/tours', icon: Map, t: 'Multi-Day Tours', s: 'Guided & road-trip tours, compared across partners', tag: 'New' },
            { to: '/flights', icon: Globe2, t: 'Explore by Country', s: 'Find the cheapest country-to-country routes', tag: 'Beta' },
          ].map((l) => (
            <Link key={l.to} to={l.to} className="group relative rounded-2xl border border-border bg-card p-5 card-elevate flex items-center gap-4" data-testid={`home-vertical-${l.t.toLowerCase().replace(/ /g, '-')}`}>
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-accent/10 text-accent shrink-0"><l.icon className="h-6 w-6" /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><p className="font-display font-semibold">{l.t}</p><span className="text-[10px] font-semibold text-accent bg-accent/10 px-1.5 py-0.5 rounded-full">{l.tag}</span></div>
                <p className="text-sm text-muted-foreground">{l.s}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
            </Link>
          ))}
        </div>
      </section>

      {/* Popular multi-day tours */}
      {featTours.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-testid="home-tours-lane">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Map className="h-5 w-5 text-accent" /> Popular multi-day tours</h2>
            <Link to="/tours" className="text-sm text-accent font-medium inline-flex items-center gap-1">View all tours <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {featTours.map((t) => <TourCard key={t.tour_id} t={t} currency={currency} />)}
          </div>
        </section>
      )}

      {/* DB-driven homepage sections (Content + Merchandising engine) */}
      {sections.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2" data-testid="home-cms-sections">
          <div className="grid gap-4 md:grid-cols-3">
            {sections.map((s) => (
              <div key={s.section_id} className="relative rounded-2xl overflow-hidden border border-border bg-card p-5 card-elevate" data-testid={`home-section-${s.section_id}`}>
                {s.sponsored && <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 bg-violet-500/10 px-2 py-0.5 rounded-full" data-testid={`home-section-sponsored-${s.section_id}`}><Megaphone className="h-3 w-3" />{s.sponsored.sponsor_label}</span>}
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent bg-accent/10 px-2 py-0.5 rounded-full">{(s.type || 'section').replace(/_/g, ' ')}</span>
                <p className="mt-2 font-display text-lg font-semibold">{s.title}</p>
                {s.subtitle && <p className="text-sm text-muted-foreground">{s.subtitle}</p>}
                {s.cta && <Button variant="outline" className="rounded-xl mt-3" onClick={() => s.cta_link && (window.location.href = s.cta_link)}>{s.cta}</Button>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Deals */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-5">
          <div><h2 className="font-display text-2xl font-semibold tracking-tight">Trending deals</h2><p className="text-sm text-muted-foreground">Ranked by our merchandising engine — sponsored items are labelled</p></div>
          <Link to="/deals"><Button variant="outline" className="rounded-xl" data-testid="see-all-deals">All deals <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {deals.map((d) => (
            <div key={d.id} className="relative" data-testid={`trending-deal-${d.id}`}>
              {d.is_sponsored && <span className="absolute z-10 top-2 left-2 inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 bg-white/95 border border-violet-200 px-2 py-0.5 rounded-full shadow-sm" data-testid={`trending-sponsored-${d.id}`}><Megaphone className="h-3 w-3" />{d.sponsor_label || 'Sponsored'}</span>}
              <DealCard deal={d} currency={currency} onViewDeal={(deal) => openDeal({ type: deal.type, offer: deal, surface: 'homepage', source: '/' })} />
            </div>
          ))}
        </div>
      </section>

      {/* Destinations */}
      <section className="section-wash">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-5">Popular destinations</h2>
          <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {destinations.map((d) => (
              <Link key={d.slug} to={`/destinations/${d.slug}`} className="group relative rounded-2xl overflow-hidden aspect-[3/4] card-elevate" data-testid={`destination-${d.slug}`}>
                <img src={d.image} alt={d.city} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/85 to-transparent" />
                <div className="absolute bottom-3 left-3 text-primary-foreground">
                  <p className="font-display font-semibold">{d.city}</p>
                  <p className="text-[11px] opacity-80">{d.country}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Creator Itineraries */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-5">
          <div><h2 className="font-display text-2xl font-semibold tracking-tight">Travel plans from people who actually travel.</h2><p className="text-sm text-muted-foreground">Premium creator itineraries you can turn into a real trip</p></div>
          <Link to="/itineraries"><Button variant="outline" className="rounded-xl" data-testid="home-explore-itineraries">Explore itineraries <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
        </div>
        {featItins.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center">
            <Sparkles className="h-8 w-8 mx-auto mb-2 text-accent" />
            <p className="font-medium">Creator itineraries are launching</p>
            <p className="text-sm text-muted-foreground">Are you a travel creator? <Link to="/creators/join" className="text-accent">Join the program</Link>.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {featItins.map((it) => <ItineraryCard key={it.itinerary_id} it={it} currency={currency} />)}
          </div>
        )}
      </section>

      {/* Featured creators */}
      {featCreators.length > 0 && (
        <section className="section-wash">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-end justify-between mb-5">
              <h2 className="font-display text-2xl font-semibold tracking-tight">Featured creators</h2>
              <Link to="/creators/join"><Button variant="outline" className="rounded-xl" data-testid="home-become-creator">Become a creator</Button></Link>
            </div>
            <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
              {featCreators.map((c) => (
                <Link key={c.creator_id} to={`/creators/${c.username}`} className="rounded-2xl border border-border bg-card p-4 text-center card-elevate" data-testid={`home-creator-${c.username}`}>
                  <span className="grid place-items-center h-14 w-14 mx-auto rounded-2xl bg-accent text-accent-foreground text-xl font-semibold overflow-hidden">{c.profile_photo ? <img src={c.profile_photo} alt="" className="h-full w-full object-cover" /> : (c.name||'C').charAt(0)}</span>
                  <p className="mt-2 font-semibold text-sm truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground truncate">@{c.username}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{(c.follower_count||0).toLocaleString()} followers</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AI teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="rounded-3xl overflow-hidden bg-primary text-primary-foreground p-8 md:p-12 relative">
          <div className="section-wash absolute inset-0 opacity-60" />
          <div className="relative max-w-xl">
            <span className="inline-flex items-center gap-1.5 text-accent text-sm font-semibold"><Sparkles className="h-4 w-4" /> AI Trip Planner</span>
            <h2 className="mt-2 font-display text-3xl font-semibold">Tell us your budget. We'll plan the trip.</h2>
            <p className="mt-2 text-primary-foreground/80">"I have ₹70,000 and 7 days from Kolkata. I want beaches and nightlife." Get a smart itinerary in seconds.</p>
            <Link to="/ai-travel-planner"><Button className="mt-5 h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="home-ai-cta">Plan my trip <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
